import React, { useEffect, useId, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import PageSeo from '@/seo/PageSeo';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRightIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  Squares2X2Icon,
  DocumentChartBarIcon,
  BellAlertIcon,
  BoltIcon,
} from '@heroicons/react/24/solid';
import styles from './PlansConversionLandingPage.module.css';
import SharedBadge from '@/global/Badge/Badge';
import SharedButton from '@/global/Button/Button';
import DemoComparisonSection from './plansConversion/DemoComparisonSection';
import { releasesApiService } from '@/services/releasesApiService';
import { apiReleaseToVersionsArray } from '@/components/About/releaseApiMappers';
import { compareVersions } from '@/components/About/helpers';
import type { StatsJSON, VersionInfo, VersionStatistics } from '@/components/About/types';

type GapGaugeDatum = { label: string; current: number; total: number };

const GAP_GAUGE_ROWS: GapGaugeDatum[][] = [
  [
    { label: 'Genes', current: 14_400, total: 30_500 },
    { label: 'Variants', current: 954_234, total: 1_672_000 },
    { label: 'Diseases', current: 18_000, total: 50_000 },
  ],
  [
    { label: 'Gene-Disease Associations', current: 100_000, total: 2_150_000 },
    { label: 'Variant-Disease Associations', current: 1_440_000, total: 4_750_000 },
    { label: 'Disease-Disease Associations', current: 308_000, total: 28_000_000 },
  ],
];

function sumGapHidden(rows: GapGaugeDatum[][]): number {
  return rows.flat().reduce((acc, g) => acc + Math.max(0, g.total - g.current), 0);
}

const TOTAL_GAP_HIDDEN = sumGapHidden(GAP_GAUGE_ROWS);

function humanCount(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    const rounded = Math.round(m * 10) / 10;
    const s = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
    return `${s} million`;
  }
  return n.toLocaleString('en-US');
}

function formatMillionsShort(n: number): string {
  const m = n / 1_000_000;
  const rounded = Math.round(m * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded} million` : `${rounded.toFixed(1)} million`;
}

function toCompact(n: number | undefined): string {
  if (!n) return '—';
  if (n >= 1_000_000) {
    const v = n / 1_000_000;
    return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}M+`;
  }
  if (n >= 1_000) return `${Math.floor(n / 1_000)}K+`;
  return n.toLocaleString();
}

function toDisplayPercent(current: number, total: number): number {
  if (total <= 0) return 0;
  const pct = Math.round((100 * current) / total);
  if (pct === 0 && current > 0) return 1;
  return Math.min(100, Math.max(0, pct));
}

type KeyStatRow = { n: string; c: string };

function buildKeyStats(statistics: VersionStatistics): KeyStatRow[] {
  const ddaCount = statistics.dda_summary ?? statistics.dda ?? 0;
  return [
    { n: toCompact(statistics.gda_summary), c: 'Gene-Disease Associations' },
    { n: toCompact(statistics.vda_summary), c: 'Variant-Disease Associations' },
    { n: toCompact(statistics.publication), c: 'Publications' },
    ...(ddaCount > 0 ? [{ n: toCompact(ddaCount), c: 'Disease-Disease Associations' }] : []),
  ];
}

const FALLBACK_KEY_STATS: KeyStatRow[] = [
  { n: '2.1M+', c: 'Gene-Disease Associations' },
  { n: '4.8M+', c: 'Variant-Disease Associations' },
  { n: '1.5M+', c: 'Publications' },
  { n: '24.4M+', c: 'Disease-Disease Associations' },
];

const fadeUp = (reduceMotion: boolean) => ({
  initial: reduceMotion ? false : { opacity: 0, y: 22 },
  whileInView: reduceMotion ? undefined : { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-36px' },
  transition: { duration: 0.48, ease: [0.22, 1, 0.36, 1] as const },
});

function DonutGauge({ item }: { item: GapGaugeDatum }) {
  const gid = useId().replace(/:/g, '');
  const r = 54;
  const cx = 66;
  const cy = 66;
  const c = 2 * Math.PI * r;
  const value = toDisplayPercent(item.current, item.total);
  const dash = (Math.min(100, Math.max(0, value)) / 100) * c;
  const mutedLabel = `${humanCount(item.current)} of ${humanCount(item.total)}`;

  return (
    <div className={styles.donutCell}>
      <div className={styles.donutRing}>
        <svg className={styles.donutSvg} viewBox="0 0 132 132" aria-hidden>
          <circle className={styles.donutTrack} cx={cx} cy={cy} r={r} />
          <circle
            className={styles.donutProgress}
            cx={cx}
            cy={cy}
            r={r}
            stroke={`url(#donutg-${gid})`}
            strokeDasharray={`${dash} ${c}`}
          />
          <defs>
            <linearGradient id={`donutg-${gid}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#e91e63" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
          </defs>
        </svg>
        <span className={styles.donutPct}>{value}%</span>
      </div>
      <p className={styles.donutLbl}>{item.label}</p>
      <p className={styles.donutLblMuted}>{mutedLabel}</p>
    </div>
  );
}

function FeatureTile({
  icon: Icon,
  text,
}: {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}) {
  return (
    <div className={styles.featureCard}>
      <div className={styles.featureIconWrap}>
        <Icon className={styles.iconMd} aria-hidden />
      </div>
      <p className={styles.featureBody}>{text}</p>
    </div>
  );
}

export default function PlansConversionLandingPage() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const [keyStats, setKeyStats] = useState<KeyStatRow[]>(FALLBACK_KEY_STATS);

  useEffect(() => {
    let cancelled = false;

    async function loadLatestKeyStats() {
      try {
        const statsRes = await fetch('/stats.json');
        const statsData: StatsJSON = statsRes.ok ? ((await statsRes.json()) as StatsJSON) : { current: '' };

        const statsVersions = Object.keys(statsData).filter(k => k !== 'current');
        const apiReleasesList = await releasesApiService.getReleasesList();
        const apiVersions = apiReleasesList.map(r => r.version);
        const allVersions = [...new Set([...statsVersions, ...apiVersions])];

        if (allVersions.length === 0) return;

        const sorted = allVersions.sort((a, b) => compareVersions(b, a));
        const latestVersion = sorted[0];

        const latestFromStats = statsData[latestVersion];
        let statistics: VersionStatistics | undefined;

        if (latestFromStats && typeof latestFromStats !== 'string') {
          statistics = (latestFromStats as VersionInfo).statistics;
        } else {
          const latestRelease = await releasesApiService.getRelease(latestVersion);
          const mapped = apiReleaseToVersionsArray(latestRelease);
          statistics = mapped.statistics;
        }

        if (!statistics || cancelled) return;

        setKeyStats(buildKeyStats(statistics));
      } catch (error) {
        // Keep fallback values if stats fail to load.
        console.error('Error loading key stats:', error);
      }
    }

    loadLatestKeyStats();
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollToDemoComparison = () => {
    document.getElementById('demo-comparison')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goPlans = () => navigate('/plans');

  return (
    <React.Fragment>
      <PageSeo
        title="Unlock every gene–disease association | DISGENET"
        description="DISGENET puts curated gene-disease and variant-disease evidence in one platform. Compare plans for API access, advanced search and target identification."
        path="/unlock-disgenet"
      />
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className={styles.page}>
        <div className={styles.bgLayer} aria-hidden>
          <div className={styles.blobPink} />
          <div className={styles.blobPurple} />
        </div>

        <section className={styles.heroSection}>
          <div className={styles.inner}>
            <motion.div {...fadeUp(!!reduceMotion)} className={styles.textCenter}>
              <SharedBadge variant="primaryLight" size="md" text="Connect all the data available" />
              <h1 className={styles.heroTitle}>Millions of findings are outside your plan</h1>
              <p className={styles.heroLead}>
                Piecing together evidence from different sources is slow and tedious. DISGENET puts all the
                disease genomics data you need in a single platform.
              </p>
              <div className={styles.ctaRow}>
                <SharedButton
                  variant="primary"
                  size="lg"
                  onClick={goPlans}
                  iconRight={<ArrowRightIcon className={styles.iconMd} aria-hidden />}
                  style={{ fontWeight: '500' }}
                >
                  Upgrade your plan now
                </SharedButton>
                <SharedButton
                  variant="secondaryOutline"
                  size="lg"
                  onClick={scrollToDemoComparison}
                  iconRight={<ArrowRightIcon className={styles.iconMd} aria-hidden />}
                  style={{ fontWeight: '500' }}
                >
                  See what I&apos;m missing
                </SharedButton>
              </div>
            </motion.div>

            <motion.div {...fadeUp(!!reduceMotion)} className={styles.statsGrid}>
              {keyStats.map(row => (
                <div key={row.c} className={styles.statCard}>
                  <p className={styles.statNumber}>{row.n}</p>
                  <p className={styles.statCategory}>{row.c}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        <DemoComparisonSection reduceMotion={!!reduceMotion} fadeUp={fadeUp} />

        <section id="data-gap" className={`${styles.sectionMuted} ${styles.sectionGap}`}>
          <div className={styles.inner}>
            <motion.div {...fadeUp(!!reduceMotion)} className={styles.textCenter}>
              <p className={styles.sectionKicker}>Your current plan</p>
              <h2 className={styles.sectionTitle}>You are seeing less than half of data</h2>
            </motion.div>

            <motion.div {...fadeUp(!!reduceMotion)} className={styles.dataCard}>
              <div className={styles.gaugeMatrix}>
                {GAP_GAUGE_ROWS.map((row, rowIdx) => (
                  <React.Fragment key={rowIdx}>
                    {rowIdx > 0 ? <div className={styles.gaugeDivider} aria-hidden /> : null}
                    <div className={styles.gaugeRow3}>
                      {row.map(item => (
                        <DonutGauge key={item.label} item={item} />
                      ))}
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <div className={styles.bannerDark}>
                <p className={styles.sectionKicker} style={{ color: '#fff' }}>
                  You are missing
                </p>
                <p className={styles.bannerTitle}>
                  <span className={styles.gradientText}>{formatMillionsShort(TOTAL_GAP_HIDDEN)}</span>{' '}
                  associations
                </p>
                <small className="text-xs text-gray-400">
                  <strong className="text-white text-sm">99%</strong> of the disease-disease associations are
                  outside your plan
                </small>
              </div>
            </motion.div>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.inner}>
            <motion.div
              {...fadeUp(!!reduceMotion)}
              className={`${styles.textCenter} ${styles.mbSectionTitle}`}
            >
              <p className={styles.sectionKicker}>What you can do</p>
              <h2 className={styles.sectionTitle}>Everything you will be able to do</h2>
            </motion.div>

            <div className={styles.featureGrid}>
              <motion.div {...fadeUp(!!reduceMotion)}>
                <FeatureTile
                  icon={MagnifyingGlassIcon}
                  text="Identify previously hidden gene-disease or variant-disease associations."
                />
              </motion.div>
              <motion.div {...fadeUp(!!reduceMotion)}>
                <FeatureTile
                  icon={FunnelIcon}
                  text="Prioritize variants for sequencing panels or functional studies."
                />
              </motion.div>
              <motion.div {...fadeUp(!!reduceMotion)}>
                <FeatureTile
                  icon={Squares2X2Icon}
                  text="Explore comorbidities using current literature, biobanks, and clinical trials."
                />
              </motion.div>
              <motion.div {...fadeUp(!!reduceMotion)}>
                <FeatureTile
                  icon={DocumentChartBarIcon}
                  text="Investigate drug-gene-disease relationships."
                />
              </motion.div>
              <motion.div {...fadeUp(!!reduceMotion)}>
                <FeatureTile
                  icon={BellAlertIcon}
                  text="Generate hypotheses for novel drug targets or rare disease mechanisms."
                />
              </motion.div>
              <motion.div {...fadeUp(!!reduceMotion)}>
                <Link to="/plans" className={styles.featureCta}>
                  <div className={styles.featureCtaBody}>
                    <BoltIcon className={styles.iconLg} style={{ color: '#ec4899' }} aria-hidden />
                    <h3 className={styles.featureCtaTitle}>Discover all the features</h3>
                  </div>
                  <ArrowRightIcon className={`${styles.iconLg} ${styles.featureCtaArrow}`} aria-hidden />
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        <section className={styles.pbSection}>
          <div className={styles.inner}>
            <motion.div {...fadeUp(!!reduceMotion)} className={styles.testimonial}>
              <span className={styles.quoteMark} aria-hidden>
                &ldquo;
              </span>
              <blockquote className={styles.quote}>
                With autism from WES data. It helped us focus our search on genes linked to neurodevelopmental
                disorders and autism comorbidities, prioritize variants using GDA scores, and integrate them
                into our scoring system, aiming at prioritizing autism-related variants for further
                investigation.
              </blockquote>
              <footer className={styles.quoteFooter}>
                <div className={styles.quoteAuthorBlock}>
                  <div>
                    <p className={styles.quoteName}>Nadav Golden</p>
                    <p className={styles.quoteRole}>
                      Bioinformatics, Azrieli National Centre for Autism and Neurodevelopment Research
                    </p>
                  </div>
                </div>
              </footer>
            </motion.div>
          </div>
        </section>

        <section className={styles.sectionMuted}>
          <div className={`${styles.inner} ${styles.valueRow}`}>
            {[
              {
                icon: (
                  <img src="/static/images/icons/iconsPink/050.svg" alt="Citations" className="w-14 h-14" />
                ),
                title: 'Over 9,200 citations',
              },
              {
                icon: (
                  <img src="/static/images/icons/iconsPink/012.svg" alt="Citations" className="w-14 h-14" />
                ),
                title: 'Used by 16,000+ researchers worldwide',
              },
              {
                icon: (
                  <img src="/static/images/icons/iconsPink/045.svg" alt="Citations" className="w-14 h-14" />
                ),
                title: 'Trusted since 2010',
              },
            ].map(item => (
              <motion.div key={item.title} {...fadeUp(!!reduceMotion)} className={styles.valueItem}>
                <span className={styles.valueIcon}>{item.icon}</span>
                <h3 className={styles.valueTitle}>{item.title}</h3>
              </motion.div>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.inner}>
            <motion.div
              {...fadeUp(!!reduceMotion)}
              className={`${styles.textCenter} ${styles.mbSectionTitle}`}
            >
              <p className={styles.sectionKicker}>Choose your plan</p>
              <h2 className={styles.sectionTitle}>Unlock every association</h2>
            </motion.div>

            <div className={styles.pricingGrid}>
              <motion.div {...fadeUp(!!reduceMotion)} className={styles.priceCard}>
                <h3 className={styles.priceTitle}>STANDARD</h3>
                <ul className={styles.list}>
                  {[
                    'Full dataset access',
                    'Web platform',
                    '30.5K+ genes · 1.7M+ variants · 50.2K+ diseases',
                    'Enrichment analysis',
                    'Advanced search',
                    'AIDA',
                  ].map(line => (
                    <li key={line} className={styles.listItem}>
                      <CheckIcon className={styles.checkMuted} aria-hidden />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <SharedButton
                  variant="secondaryOutline"
                  size="lg"
                  onClick={goPlans}
                  iconRight={<ArrowRightIcon className={styles.iconSm} aria-hidden />}
                  className="mt-4"
                >
                  Upgrade my plan
                </SharedButton>
              </motion.div>

              <motion.div {...fadeUp(!!reduceMotion)} className={styles.priceCardFeatured}>
                <span className="row justify-end">
                  <SharedBadge variant="primary" size="md" text="MOST POPULAR" />
                </span>
                <h3 className={styles.priceTitleLight}>ADVANCED</h3>
                <ul className={styles.list}>
                  {[
                    'Full dataset access',
                    'Web platform, REST API, R package & Cytoscape App access',
                    '30.5K+ genes · 1.7M+ variants · 50.2K+ diseases',
                    'Enrichment analysis',
                    'Advanced search',
                    'AIDA',
                  ].map(line => (
                    <li key={line} className={styles.listItemLight}>
                      <CheckIcon className={styles.checkPink} aria-hidden />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <SharedButton
                  variant="primary"
                  size="lg"
                  onClick={goPlans}
                  iconRight={<ArrowRightIcon className={styles.iconMd} aria-hidden />}
                  className="mt-4"
                >
                  Upgrade my plan
                </SharedButton>
              </motion.div>
            </div>
          </div>
        </section>

        <section className={styles.finalSection}>
          <div className={styles.finalInner}>
            <motion.div {...fadeUp(!!reduceMotion)}>
              <h2 className={styles.finalTitle}>
                Don&apos;t let the <span className={styles.finalHighlight}>next discovery</span> stay out of
                your plan
              </h2>
              <SharedButton
                variant="primary"
                size="lg"
                onClick={goPlans}
                iconRight={<ArrowRightIcon className={styles.iconMd} aria-hidden />}
                className="mt-4"
              >
                Upgrade your plan
              </SharedButton>
            </motion.div>
          </div>
        </section>
      </div>
    </React.Fragment>
  );
}
